import { useCallback, useEffect, useRef, useState } from "react";
import { useDebounce } from "../hooks/useDebounce";
import useStore from "../hooks/useStore";
import { InactivityWarning } from "./InactivityWarning";

const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
];
const DEBOUNCE_DELAY = 100; // 100ms debounce for more responsive updates
const CHECK_INTERVAL = 1000; // 1 second interval for checking inactivity
const DEFAULT_TIMEOUT = 300; // 5 minutes
const MAX_TIMEOUT = 86400; // 1 day
const APP_TIMEOUT = 2592000; // 30 days

const getTimeoutConfig = (currentUser, subdomain) => {
  // If subdomain is 'app', return 30 days timeout with 1 minute warning
  if (subdomain === "app") {
    return {
      timeout: APP_TIMEOUT,
      warning: 60,
    };
  }
  if (subdomain === "admin") {
    return {
      timeout: MAX_TIMEOUT,
      warning: 60,
    };
  }

  let timeoutDuration = currentUser?.uiTimeout;

  // If no timeout specified or invalid, use default
  if (!timeoutDuration || isNaN(timeoutDuration)) {
    timeoutDuration = DEFAULT_TIMEOUT;
  }

  // Ensure timeout is within bounds
  timeoutDuration = Math.min(timeoutDuration, MAX_TIMEOUT);

  // Calculate warning threshold
  const warningThreshold = timeoutDuration <= 60 ? 15 : 60;

  return {
    timeout: timeoutDuration,
    warning: warningThreshold,
  };
};

export const ActivityTracker = ({ children, onTimeout }) => {
  const [showWarning, setShowWarning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const isLoggedIn = localStorage.getItem("loggedIn") === "true";
  const currentUser = useStore((state) => state.currentUser);
  const subdomain = useStore((state) => state.subdomain);
  const [timeoutConfig, setTimeoutConfig] = useState(
    getTimeoutConfig(currentUser, subdomain)
  );

  useEffect(() => {
    const config = getTimeoutConfig(currentUser, subdomain);
    setTimeoutConfig(config);
  }, [currentUser]);

  const updateLastActivity = useCallback(() => {
    if (!isPaused && isLoggedIn) {
      localStorage.setItem("lastActivity", Date.now().toString());
    }
  }, [isPaused, isLoggedIn]);
  const debouncedUpdateActivity = useDebounce(
    updateLastActivity,
    DEBOUNCE_DELAY
  );
  const checkIntervalRef = useRef(null);

  const handleStayLoggedIn = () => {
    setShowWarning(false);
    setIsPaused(false);
    // Force an immediate activity update when staying logged in
    localStorage.setItem("lastActivity", Date.now().toString());
  };

  // Setup effect - runs once on mount and when login state changes
  useEffect(() => {
    if (!isLoggedIn) {
      localStorage.removeItem("lastActivity");
      setShowWarning(false);
      setIsPaused(false);
    }

    // Add event listeners
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, debouncedUpdateActivity);
    });

    // Cleanup
    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, debouncedUpdateActivity);
      });
      debouncedUpdateActivity.cancel();
    };
  }, [debouncedUpdateActivity, isLoggedIn]); // Update when login state changes

  // Timer check effect - separate from event listeners
  useEffect(() => {
    checkIntervalRef.current = setInterval(() => {
      const lastActivityStr = localStorage.getItem("lastActivity");
      if (!lastActivityStr) {
        return; // Skip if no activity timestamp exists
      }

      const lastActivity = parseInt(lastActivityStr, 10);
      if (isNaN(lastActivity)) {
        return; // Skip if timestamp is invalid
      }

      const now = Date.now();
      const secondsSinceLastActivity = Math.floor((now - lastActivity) / 1000);

      // Only show warning if not already shown and not paused
      if (
        !isPaused &&
        !showWarning &&
        secondsSinceLastActivity >=
          timeoutConfig.timeout - timeoutConfig.warning
      ) {
        setShowWarning(true);
        setIsPaused(true);
      }

      // Handle timeout
      if (secondsSinceLastActivity >= timeoutConfig.timeout && isLoggedIn) {
        // Clean up all timeout-related state
        localStorage.removeItem("authToken");
        localStorage.removeItem("lastActivity");
        setShowWarning(false);
        setIsPaused(false);
        if (onTimeout) {
          onTimeout();
        }
      }
    }, CHECK_INTERVAL);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [isPaused, showWarning, isLoggedIn, onTimeout, timeoutConfig]); // Include all dependencies

  return (
    <>
      {showWarning && (
        <InactivityWarning
          onStayLoggedIn={handleStayLoggedIn}
          warningSeconds={timeoutConfig.warning}
        />
      )}
      {children}
    </>
  );
};
